// Builder ▸ Add ▸ NPC. Pick a look (or one of your existing characters), an
// animation and a name; the character is built on the normal avatar pipeline
// and the NPC is placed with a click while it builds.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Dices, Loader, Brain, RefreshCw, Users } from 'lucide-react';
import {
  NPC_GENDERS, NPC_ETHNICITIES, NPC_STYLES, NPC_TYPES, NPC_ANIMS, RANDOM,
  randomName, randomOf, npcPrompt, type NpcConfig, type NpcGender, type NpcEthnicity, type NpcTalk,
} from '../../config/npc';
import { generateCharacterImage } from '../../services/openRouterApi';
import { fetchMyAvatars, fetchMyNpcAvatars, type Avatar } from '../../services/avatarService';
import { fetchOnboardingStatus } from '../../services/onboarding';
import { startNpcBuild } from '../../services/npcBuild';
import { AvatarBuildError } from '../../services/tripoAvatarBuild';
import { NpcTalkSettings } from './NpcTalkSettings';
import './NpcCreator.css';

type Tab = 'look' | 'anim' | 'advanced';
const PREVIEW_LIMIT = 90;
type Source = 'new' | 'mine';

interface Props {
  onClose: () => void;
  /** Hand the finished config to the builder to place with a click. */
  onPlace: (npc: NpcConfig) => void;
}

const Chips: React.FC<{ options: string[]; value: string; onChange: (v: string) => void }> = ({ options, value, onChange }) => (
  <div className="npc-chips">
    {options.map((o) => (
      <button key={o} type="button" className={o === value ? 'on' : ''} onClick={() => onChange(o)}>{o}</button>
    ))}
  </div>
);

const NpcCreator: React.FC<Props> = ({ onClose, onPlace }) => {
  const [tab, setTab] = useState<Tab>('look');
  const [source, setSource] = useState<Source>('new');
  const [gender, setGender] = useState<NpcGender>('Female');
  const [ethnicity, setEthnicity] = useState<NpcEthnicity>('African American');
  const [type, setType] = useState('Casual');
  const [clothing, setClothing] = useState(RANDOM);
  const [style, setStyle] = useState('Realistic');
  const [extra, setExtra] = useState('');
  const [name, setName] = useState(() => randomName('Female', 'African American'));
  const [nameTouched, setNameTouched] = useState(false);
  const [anim, setAnim] = useState('idle');
  const [radius, setRadius] = useState(3);
  const [talk, setTalk] = useState<NpcTalk | undefined>(undefined);

  const [preview, setPreview] = useState<{ image: string; outfit: string } | null>(null);
  const [busy, setBusy] = useState<'' | 'image' | 'build'>('');
  const [error, setError] = useState('');
  const [canBuild, setCanBuild] = useState<boolean | null>(null);
  const [previewErr, setPreviewErr] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const drawing = useRef<AbortController | null>(null);

  const [mine, setMine] = useState<Avatar[] | null>(null);
  const [picked, setPicked] = useState<Avatar | null>(null);

  useEffect(() => { fetchOnboardingStatus().then((s) => setCanBuild(s ? s.can_build : null)); }, []);
  useEffect(() => {
    if (source !== 'mine' || mine) return;
    Promise.all([fetchMyNpcAvatars(), fetchMyAvatars()])
      .then(([n, w]) => setMine([...n, ...w].filter((a) => a.model_url)))
      .catch(() => setMine([]));
  }, [source, mine]);

  // A fresh name to match a new gender / background, until they type their own.
  useEffect(() => { if (!nameTouched) setName(randomName(gender, ethnicity)); }, [gender, ethnicity, nameTouched]);
  // A changed look invalidates the preview.
  useEffect(() => { setPreview(null); }, [gender, ethnicity, type, clothing, style]);

  const outfits = useMemo(() => NPC_TYPES.find((t) => t.id === type)?.outfits ?? [], [type]);
  useEffect(() => { if (clothing !== RANDOM && !outfits.includes(clothing)) setClothing(RANDOM); }, [outfits, clothing]);

  const moving = NPC_ANIMS.find((a) => a.id === anim)?.moves;
  const groups = useMemo(() => [...new Set(NPC_ANIMS.map((a) => a.group))], []);

  // The drawing usually takes 10 to 20 s. Count it up so it never looks frozen,
  // and give up after PREVIEW_LIMIT s so a lost response can't hang the modal.
  useEffect(() => {
    if (busy !== 'image') return;
    setElapsed(0);
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [busy]);
  useEffect(() => () => drawing.current?.abort(), []);

  const makePreview = async () => {
    setError('');
    setPreviewErr('');
    setBusy('image');
    const ctrl = new AbortController();
    drawing.current = ctrl;
    const timer = setTimeout(() => ctrl.abort('timeout'), PREVIEW_LIMIT * 1000);
    try {
      const outfit = clothing === RANDOM ? randomOf(outfits) : clothing;
      const img = await generateCharacterImage(npcPrompt({ gender, ethnicity, type, clothing: outfit, style }, outfit, extra), ctrl.signal);
      if (drawing.current === ctrl) setPreview({ image: img.dataUrl, outfit });
    } catch (e: any) {
      if (drawing.current === ctrl) {
        setPreviewErr(ctrl.signal.aborted
          ? ctrl.signal.reason === 'timeout' ? 'That took too long. Try again.' : ''
          : e?.message || 'Could not make a preview. Try again.');
      }
    } finally {
      clearTimeout(timer);
      if (drawing.current === ctrl) { drawing.current = null; setBusy(''); }
    }
  };
  const cancelPreview = () => { drawing.current?.abort('cancel'); drawing.current = null; setBusy(''); };

  const base = (): Omit<NpcConfig, 'status'> => ({
    name: name.trim() || randomName(gender, ethnicity),
    gender, ethnicity, type, clothing: preview?.outfit ?? clothing, style, anim, radius,
    ...(talk ? { talk } : {}),
  });

  const place = async () => {
    setError('');
    if (source === 'mine') {
      if (!picked) return;
      onPlace({ ...base(), status: 'ready', avatarUrl: picked.model_url, avatarId: picked.id, thumbnailUrl: picked.thumbnail_url || undefined });
      return;
    }
    if (!preview) return;
    setBusy('build');
    try {
      const buildId = await startNpcBuild(preview.image, { ...base(), status: 'building' });
      onPlace({ ...base(), status: 'building', buildId });
    } catch (e: any) {
      setError(e instanceof AvatarBuildError && e.needsPlan
        ? 'Building new characters needs an avatar plan or code. You can still place one of your existing characters.'
        : e?.message || 'Could not start the build.');
      setBusy('');
    }
  };

  const ready = source === 'mine' ? !!picked : !!preview;

  return createPortal(
    <div className="npc-backdrop" onPointerDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="npc-modal" role="dialog" aria-label="Add an NPC" onPointerDown={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        <header className="npc-head">
          <div>
            <h2>Add an NPC</h2>
            <p>A character that lives in your world.</p>
          </div>
          <button className="npc-x" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </header>

        <div className="npc-name">
          <label htmlFor="npc-name">Name</label>
          <div>
            <input id="npc-name" value={name} maxLength={24} onChange={(e) => { setName(e.target.value); setNameTouched(true); }} />
            <button type="button" title="Random name" onClick={() => { setName(randomName(gender, ethnicity)); setNameTouched(false); }}><Dices size={16} /></button>
          </div>
        </div>

        <nav className="npc-tabs">
          <button className={tab === 'look' ? 'on' : ''} onClick={() => setTab('look')}>Look</button>
          <button className={tab === 'anim' ? 'on' : ''} onClick={() => setTab('anim')}>Animation</button>
          <button className={tab === 'advanced' ? 'on' : ''} onClick={() => setTab('advanced')}>Advanced</button>
        </nav>

        <div className="npc-body">
          {tab === 'look' && (
            <>
              <div className="npc-source">
                <button className={source === 'new' ? 'on' : ''} onClick={() => setSource('new')}>New character</button>
                <button className={source === 'mine' ? 'on' : ''} onClick={() => setSource('mine')}><Users size={14} /> My characters</button>
              </div>

              {source === 'new' ? (
                <div className="npc-look">
                  <div className="npc-fields">
                    <div className="npc-field"><span>Gender</span><Chips options={NPC_GENDERS} value={gender} onChange={(v) => setGender(v as NpcGender)} /></div>
                    <div className="npc-field"><span>Background</span><Chips options={NPC_ETHNICITIES} value={ethnicity} onChange={(v) => setEthnicity(v as NpcEthnicity)} /></div>
                    <div className="npc-field"><span>Type</span><Chips options={NPC_TYPES.map((t) => t.id)} value={type} onChange={setType} /></div>
                    <div className="npc-field">
                      <span>Clothing</span>
                      <select value={clothing} onChange={(e) => setClothing(e.target.value)}>
                        <option value={RANDOM}>Random {type.toLowerCase()} outfit</option>
                        {outfits.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    <div className="npc-field"><span>Style</span><Chips options={NPC_STYLES} value={style} onChange={setStyle} /></div>
                    <div className="npc-field">
                      <span>Details <em>(optional)</em></span>
                      <input value={extra} maxLength={160} placeholder="e.g. grey beard, round glasses, red sneakers" onChange={(e) => setExtra(e.target.value)} />
                    </div>
                  </div>
                  <div className="npc-preview">
                    {preview ? <img src={preview.image} alt={`Preview of ${name}`} /> : (
                      <div className="npc-preview-empty">
                        {busy === 'image' ? <><Loader className="npc-spin" size={22} /> Drawing {name}… {elapsed}s</>
                          : previewErr ? <span className="npc-preview-err">{previewErr}</span>
                          : 'Preview the look before building'}
                      </div>
                    )}
                    {busy === 'image' ? (
                      <button type="button" className="npc-ghost" onClick={cancelPreview}>Cancel</button>
                    ) : (
                      <button type="button" className="npc-ghost" disabled={!!busy || canBuild === false} onClick={makePreview}>
                        {preview ? <><RefreshCw size={14} /> Try another</> : previewErr ? 'Try again' : 'Preview look'}
                      </button>
                    )}
                    {preview && <small>{preview.outfit}</small>}
                  </div>
                </div>
              ) : (
                <div className="npc-mine">
                  {mine === null ? <p className="npc-muted"><Loader className="npc-spin" size={16} /> Loading your characters…</p>
                    : mine.length === 0 ? <p className="npc-muted">No characters yet. Make a new one.</p>
                    : mine.map((a) => (
                      <button key={a.id} className={picked?.id === a.id ? 'on' : ''} onClick={() => { setPicked(a); if (!nameTouched) setName(a.name.replace(/^NPC: /, '')); }}>
                        {a.thumbnail_url ? <img src={a.thumbnail_url} alt="" /> : <span className="npc-noimg" />}
                        <span>{a.name.replace(/^NPC: /, '')}</span>
                      </button>
                    ))}
                </div>
              )}
              {source === 'new' && canBuild === false && (
                <p className="npc-warn">New characters use an avatar build, which needs a plan or code. <a href="/council" target="_blank" rel="noopener">See plans</a>. You can still place one of your existing characters.</p>
              )}
            </>
          )}

          {tab === 'anim' && (
            <div className="npc-anims">
              {groups.map((g) => (
                <div key={g} className="npc-field">
                  <span>{g}</span>
                  <div className="npc-chips">
                    {NPC_ANIMS.filter((a) => a.group === g).map((a) => (
                      <button key={a.id} type="button" className={a.id === anim ? 'on' : ''} onClick={() => setAnim(a.id)}>{a.label}</button>
                    ))}
                  </div>
                </div>
              ))}
              {moving && (
                <div className="npc-field">
                  <span>Wanders up to {radius} m from where you place it</span>
                  <input type="range" min={1} max={12} step={1} value={radius} onChange={(e) => setRadius(+e.target.value)} />
                </div>
              )}
              <p className="npc-muted">The move loops for as long as people are in the world. You can change it later in the NPC's panel.</p>
            </div>
          )}

          {tab === 'advanced' && (
            <div className="npc-soon">
              <NpcTalkSettings talk={talk} gender={gender} name={name} onChange={setTalk} />
              {[
                { icon: <Brain size={18} />, t: 'Memory', d: 'Remembers people it has talked to, and can guide or host events.' },
              ].map((f) => (
                <div key={f.t} className="npc-soon-card">
                  {f.icon}
                  <div><b>{f.t}</b><p>{f.d}</p></div>
                  <span className="npc-soon-tag">Coming soon</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {error && <p className="npc-error">{error}</p>}
        <footer className="npc-foot">
          <span className="npc-muted">{source === 'new' ? 'Uses one avatar build. Takes a few minutes; you can keep building meanwhile.' : 'Free: reuses a character you already have.'}</span>
          <button className="npc-primary" disabled={!ready || !!busy} onClick={place}>
            {busy === 'build' ? <><Loader className="npc-spin" size={16} /> Starting…</> : source === 'new' ? 'Build & place' : 'Place'}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
};

export default NpcCreator;
